import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Icon1 from "@assets/icons/Icon";
import { useVehiculesStore } from "@store/vehiculesStore";
import { useDataStore } from "@store/user_org";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import SearchField from "@components/ui/textfields/SearchField";
import SearchAlgolia from "@components/ui/search/SearchAlgolia";
import MiniCalendar from "@components/ui/MiniCalendar";
import type { Vehicle, VehicleClass, VehicleReservation } from "../types/garage/vehicules";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import Button from "@components/ui/buttons/Button";
import DayReservationGrid from "./vehicules/DayReservationGrid";
import DataGrid from "@components/ui/tables/DataGrid";
import { transformVehiclesToTable } from "@components/ui/tables/transformToTables";
import VehiculeModal from "@components/modals/VehiculeModal";
import ReservationModal from "@components/modals/ReservationModal";
import { searchVehiclesFull } from "@services/algolia";
import TagFilterBar from "@components/ui/tags/TagFilterBar";
import { vehiclesApi } from "@services/api/vehicles";
import { showNotification } from "context/notification.service";
import { useTagStore } from "@store/tagStore";

// --- Filter section ---

const USAGE_FILTER_TABS = [
  { value: "tous", label: "Tous" },
  { value: "location", label: "Location" },
  { value: "pret", label: "Prêt" },
  { value: "velos", label: "Vélos" },
];

const CLASS_FILTER_TABS = [
  { value: "tous", label: "Tous" },
  { value: "vp", label: "VP" },
  { value: "vu", label: "VU" },
];

// --- Reservation Grid ---

const VEHICLE_COL_WIDTH = 190;
const ROW_HEIGHT = 40;

const USAGE_COLORS: Record<string, { bg: string; dot: string }> = {
  location: { bg: "#EEF2FF", dot: "#6366F1" },
  pret: { bg: "#F0FDF4", dot: "#22C55E" },
  velos: { bg: "#FFFBEB", dot: "#F59E0B" },
};

// Two header rows sit above the vehicle rows in the day-columns area
// (day names + counts), each 28px tall.
const WEEK_HEADER_HEIGHT = 56;
const WEEK_CLICK_THRESHOLD = 5; // px — below this a pointer gesture is a click

// --- Day-granular date helpers for week-view drag ---
function addDaysStr(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round(
    (new Date(by, bm - 1, bd).getTime() - new Date(ay, am - 1, ad).getTime()) /
      86_400_000,
  );
}

interface WeekDragState {
  reservationId: string;
  mode: "move" | "resize" | "resize-start";
  original: VehicleReservation;
  startX: number;
  startY: number;
  /** Whole-day delta from drag start */
  deltaDays: number;
  previewVehicleId: string;
}

/** Compute the dragged reservation's new dates + vehicle (day granularity). */
function applyWeekDrag(drag: WeekDragState): {
  startDate: string;
  endDate: string;
  vehicleId: string;
} {
  const { original, mode, deltaDays, previewVehicleId } = drag;
  let startDate = original.startDate;
  let endDate = original.endDate;
  if (mode === "move") {
    startDate = addDaysStr(original.startDate, deltaDays);
    endDate = addDaysStr(original.endDate, deltaDays);
  } else if (mode === "resize") {
    // Right edge → end date, never before the start.
    let ne = addDaysStr(original.endDate, deltaDays);
    if (daysBetween(original.startDate, ne) < 0) ne = original.startDate;
    endDate = ne;
  } else if (mode === "resize-start") {
    // Left edge → start date, never after the end.
    let ns = addDaysStr(original.startDate, deltaDays);
    if (daysBetween(ns, original.endDate) < 0) ns = original.endDate;
    startDate = ns;
  }
  return { startDate, endDate, vehicleId: previewVehicleId };
}

function ReservationGrid() {
  const getFilteredVehicles = useVehiculesStore((s) => s.getFilteredVehicles);
  const getWeekDays = useVehiculesStore((s) => s.getWeekDays);
  const reservations = useVehiculesStore((s) => s.reservations);
  const selectedDate = useVehiculesStore((s) => s.selectedDate);
  const highlightedId = useVehiculesStore((s) => s.highlightedReservationId);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [editingReservation, setEditingReservation] =
    useState<VehicleReservation | null>(null);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const updateReservation = useVehiculesStore((s) => s.updateReservation);
  const updateVehicle = useVehiculesStore((s) => s.updateVehicle);

  const dayAreaRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<WeekDragState | null>(null);
  const dragMoved = useRef(false);

  const vehicles = getFilteredVehicles();
  const weekDays = getWeekDays();

  const firstDate = weekDays[0]?.date || "";
  const lastDate = weekDays[weekDays.length - 1]?.date || "";

  // One day column width in px (day area is evenly split across weekDays).
  const dayWidthPx = () => {
    const w = dayAreaRef.current?.getBoundingClientRect().width || 0;
    return weekDays.length ? w / weekDays.length : 0;
  };

  const vehicleIndexFromY = (clientY: number) => {
    if (!dayAreaRef.current) return -1;
    const rect = dayAreaRef.current.getBoundingClientRect();
    const y = clientY - rect.top - WEEK_HEADER_HEIGHT;
    const idx = Math.floor(y / ROW_HEIGHT);
    return Math.max(0, Math.min(vehicles.length - 1, idx));
  };

  const handleResPointerDown = useCallback(
    (
      e: React.PointerEvent,
      r: VehicleReservation,
      mode: "move" | "resize" | "resize-start",
    ) => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragMoved.current = false;
      setDrag({
        reservationId: r.id,
        mode,
        original: { ...r },
        startX: e.clientX,
        startY: e.clientY,
        deltaDays: 0,
        previewVehicleId: r.vehicleId,
      });
    },
    [],
  );

  const handleAreaPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!drag) return;
      const deltaX = e.clientX - drag.startX;
      const deltaY = e.clientY - drag.startY;
      if (
        Math.abs(deltaX) > WEEK_CLICK_THRESHOLD ||
        Math.abs(deltaY) > WEEK_CLICK_THRESHOLD
      ) {
        dragMoved.current = true;
      }
      const dw = dayWidthPx();
      const deltaDays = dw ? Math.round(deltaX / dw) : 0;
      const targetVehicleId =
        drag.mode === "move"
          ? vehicles[vehicleIndexFromY(e.clientY)]?.id ?? drag.previewVehicleId
          : drag.previewVehicleId;
      setDrag((prev) =>
        prev ? { ...prev, deltaDays, previewVehicleId: targetVehicleId } : null,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drag, vehicles, weekDays.length],
  );

  const handleAreaPointerUp = useCallback(() => {
    if (!drag) return;
    if (!dragMoved.current) {
      const r = reservations.find((x) => x.id === drag.reservationId);
      if (r) setEditingReservation(r);
      setDrag(null);
      return;
    }
    const { startDate, endDate, vehicleId } = applyWeekDrag(drag);
    const changed =
      startDate !== drag.original.startDate ||
      endDate !== drag.original.endDate ||
      vehicleId !== drag.original.vehicleId;
    if (changed) {
      updateReservation(drag.reservationId, { startDate, endDate, vehicleId });
    }
    setDrag(null);
  }, [drag, reservations, updateReservation]);

  // Build a map of reservations per vehicle, filtered to visible range
  const resByVehicle = useMemo(() => {
    const map: Record<string, VehicleReservation[]> = {};
    for (const v of vehicles) map[v.id] = [];
    for (const r of reservations) {
      if (
        map[r.vehicleId] &&
        r.startDate <= lastDate &&
        r.endDate >= firstDate
      ) {
        map[r.vehicleId].push(r);
      }
    }
    return map;
  }, [vehicles, reservations, firstDate, lastDate]);

  // Compute departure/arrival counts per day
  const dayCounts = useMemo(() => {
    const counts: Record<string, { departures: number; arrivals: number }> = {};
    for (const day of weekDays) {
      let dep = 0;
      let arr = 0;
      for (const r of reservations) {
        if (r.startDate === day.date) dep++;
        if (r.endDate === day.date) arr++;
      }
      counts[day.date] = { departures: dep, arrivals: arr };
    }
    return counts;
  }, [weekDays, reservations]);

  const HOURS_START = 8;
  const HOURS_END = 22;
  const HOURS_SPAN = HOURS_END - HOURS_START; // 14

  // Actual current date+time for the blue "now" line
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const actualTodayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const actualTodayIdx = weekDays.findIndex((d) => d.date === actualTodayStr);
  const nowHour = now.getHours() + now.getMinutes() / 60;
  const clampedHour = Math.min(Math.max(nowHour, HOURS_START), HOURS_END);
  const nowPct =
    actualTodayIdx >= 0
      ? ((actualTodayIdx + (clampedHour - HOURS_START) / HOURS_SPAN) /
          weekDays.length) *
        100
      : -1;

  const getBarStyle = (
    r: VehicleReservation,
    ov?: { startDate?: string; endDate?: string },
  ) => {
    const rStart = ov?.startDate ?? r.startDate;
    const rEnd = ov?.endDate ?? r.endDate;
    // Clamp to visible range
    const sDate = rStart < firstDate ? firstDate : rStart;
    const eDate = rEnd > lastDate ? lastDate : rEnd;

    let sIdx = weekDays.findIndex((d) => d.date === sDate);
    if (sIdx < 0) sIdx = 0;

    let eIdx = weekDays.findIndex((d) => d.date === eDate);
    if (eIdx < 0) eIdx = weekDays.length - 1;

    const colCount = weekDays.length;
    const colWidth = 100 / colCount; // one day column in %

    const isSingleDay = rStart === rEnd;

    if (isSingleDay && r.startHour != null && r.durationHours != null) {
      // Single-day: position within the day column based on time
      const hourOffset = (r.startHour - HOURS_START) / HOURS_SPAN;
      const hourWidth = r.durationHours / HOURS_SPAN;
      const left = sIdx * colWidth + hourOffset * colWidth;
      const width = Math.max(0.3 * colWidth, hourWidth * colWidth);
      return { left: `${left}%`, width: `${width}%` };
    }

    // Multi-day: offset start/end columns by startHour/returnHour
    const startOffset =
      r.startHour != null && sDate === rStart
        ? (r.startHour - HOURS_START) / HOURS_SPAN
        : 0;
    const endOffset =
      r.returnHour != null && eDate === rEnd
        ? (r.returnHour - HOURS_START) / HOURS_SPAN
        : 1;

    const left = sIdx * colWidth + startOffset * colWidth;
    const right = eIdx * colWidth + endOffset * colWidth;
    let width = right - left;
    if (width < 0.3 * colWidth) width = 0.3 * colWidth;

    return { left: `${left}%`, width: `${width}%` };
  };

  return (
    <>
      <div
        className="flex flex-row"
        style={{
          border: "1px solid #D9D9D9",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        {/* Left: vehicle labels column */}
        <div style={{ width: VEHICLE_COL_WIDTH, flexShrink: 0 }}>
          {/* Header spacer (day names row) */}
          <div
            style={{
              height: 28,
              borderRight: "1px solid #D9D9D9",
              borderBottom: "1px solid #D9D9D9",
            }}
          />
          {/* Header spacer (counts row) */}
          <div
            style={{
              height: 28,
              borderRight: "1px solid #D9D9D9",
              borderBottom: "1px solid #D9D9D9",
            }}
          />
          {/* Vehicle labels */}
          {vehicles.map((vehicle) => {
            const colors = USAGE_COLORS[vehicle.usage] || USAGE_COLORS.pret;
            return (
              <div
                key={vehicle.id}
                className="flex flex-row items-center gap-1.5 cursor-pointer hover:bg-neutral-50 transition-colors"
                style={{
                  height: ROW_HEIGHT,
                  padding: "0 6px",
                  borderRight: "1px solid #D9D9D9",
                  borderBottom: "1px solid #D9D9D9",
                }}
                onClick={() => setEditingVehicle(vehicle)}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: colors.dot,
                    flexShrink: 0,
                  }}
                />
                <div className="flex flex-col min-w-0" style={{ gap: 1 }}>
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      fontSize: 11,
                      lineHeight: "130%",
                      color: "#374151",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {vehicle.name}
                  </span>
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontWeight: 400,
                      fontSize: 10,
                      lineHeight: "120%",
                      color: "#9CA3AF",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {vehicle.plate}{vehicle.km > 0 ? ` / ${vehicle.km.toLocaleString("fr-FR")} km` : ""}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: day columns area */}
        <div
          ref={dayAreaRef}
          className="flex-1 relative"
          style={{ minWidth: 0, touchAction: drag ? "none" : undefined }}
          onPointerMove={handleAreaPointerMove}
          onPointerUp={handleAreaPointerUp}
        >
          {/* Blue "now" line — precise current time */}
          {nowPct >= 0 && (
            <div
              className="absolute pointer-events-none"
              style={{
                top: 0,
                bottom: 0,
                left: `${nowPct}%`,
                width: 2,
                background: "#5C6BFF",
                zIndex: 25,
              }}
            />
          )}

          {/* Header row: day names */}
          <div className="flex flex-row" style={{ height: 28 }}>
            {weekDays.map((day) => {
              const isSelected = day.date === selectedDate;
              return (
                <div
                  key={day.date}
                  className="flex-1 flex items-center justify-center"
                  style={{
                    height: 28,
                    borderRight: "1px solid #D9D9D9",
                    borderBottom: isSelected
                      ? "2px solid #5C6BFF"
                      : "1px solid #D9D9D9",
                    background: isSelected
                      ? "#EEF0FF"
                      : day.isWeekend
                        ? "#E6E6E6"
                        : undefined,
                    fontFamily: "Poppins",
                    fontWeight: isSelected ? 600 : 500,
                    fontSize: 12,
                    color: isSelected ? "#5C6BFF" : "#A1A1A1",
                  }}
                >
                  {day.label}
                </div>
              );
            })}
          </div>

          {/* Header row: counts */}
          <div className="flex flex-row" style={{ height: 28 }}>
            {weekDays.map((day) => {
              const c = dayCounts[day.date] || { departures: 0, arrivals: 0 };
              const isSelected = day.date === selectedDate;
              return (
                <div
                  key={day.date}
                  className="flex-1 flex flex-row items-center justify-between"
                  style={{
                    height: 28,
                    padding: "5px 10px",
                    borderRight: "1px solid #D9D9D9",
                    borderBottom: "1px solid #D9D9D9",
                    background: isSelected
                      ? "#EEF0FF"
                      : day.isWeekend
                        ? "#E6E6E6"
                        : undefined,
                  }}
                >
                  <div
                    className="flex flex-row items-center"
                    style={{ gap: 5 }}
                  >
                    <span
                      style={{
                        fontFamily: "Poppins",
                        fontWeight: 500,
                        fontSize: 12,
                        color: "#A1A1A1",
                      }}
                    >
                      {c.departures}
                    </span>
                    <Icon1 name="car-reception" size={14} color="#A1A1A1" />
                  </div>
                  <div
                    className="flex flex-row items-center"
                    style={{ gap: 5 }}
                  >
                    <span
                      style={{
                        fontFamily: "Poppins",
                        fontWeight: 500,
                        fontSize: 12,
                        color: "#A1A1A1",
                      }}
                    >
                      {c.arrivals}
                    </span>
                    <Icon1 name="car-restitution" size={14} color="#A1A1A1" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Vehicle rows */}
          {vehicles.map((vehicle) => {
            const vReservations = resByVehicle[vehicle.id] || [];
            return (
              <div
                key={vehicle.id}
                className="flex flex-row relative"
                style={{ height: ROW_HEIGHT }}
              >
                {/* Day background cells */}
                {weekDays.map((day) => {
                  const isSelected = day.date === selectedDate;
                  return (
                    <div
                      key={day.date}
                      className="flex-1"
                      style={{
                        height: ROW_HEIGHT,
                        borderRight: "1px solid #D9D9D9",
                        borderBottom: "1px solid #D9D9D9",
                        background: isSelected
                          ? "#F5F6FF"
                          : day.isWeekend
                            ? "#E6E6E6"
                            : undefined,
                      }}
                    />
                  );
                })}

                {/* Reservation bars */}
                {vReservations.map((r) => {
                  const barStyle = getBarStyle(r);
                  const isHighlighted = highlightedId === r.id;
                  const isDragging = drag?.reservationId === r.id;
                  const isHovered = hoveredId === r.id && !drag;
                  const fmtDate = (d: string) =>
                    d.split("-").reverse().join("/");
                  const fmtHour = (h: number) =>
                    `${Math.floor(h)}h${String(Math.round((h % 1) * 60)).padStart(2, "0")}`;
                  return (
                    <div
                      key={r.id}
                      data-reservation-id={r.id}
                      className={`absolute flex flex-row items-stretch${isHighlighted ? " animate-pulse" : ""}`}
                      style={{
                        top: 0,
                        bottom: 0,
                        left: barStyle.left,
                        width: barStyle.width,
                        background: isHighlighted ? "#A5B4FC" : "#CACFFF",
                        border: isHighlighted
                          ? "2px solid #6366f1"
                          : "1px solid #B7BDFF",
                        boxShadow: isHighlighted
                          ? "0px 0px 12px rgba(99, 102, 241, 0.5)"
                          : "0px 2px 4px rgba(0, 0, 0, 0.15)",
                        borderRadius: 5,
                        zIndex: isHovered ? 30 : isHighlighted ? 20 : 10,
                        overflow: "visible",
                        cursor: "grab",
                        opacity: isDragging ? 0.3 : 1,
                        touchAction: "none",
                      }}
                      onMouseEnter={() => setHoveredId(r.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onPointerDown={(e) => handleResPointerDown(e, r, "move")}
                    >
                      {/* Left resize handle — drag to change the start day */}
                      <div
                        style={{
                          position: "absolute",
                          left: -3,
                          top: 0,
                          bottom: 0,
                          width: 10,
                          cursor: "ew-resize",
                          zIndex: 2,
                        }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          handleResPointerDown(e, r, "resize-start");
                        }}
                      />
                      {/* Right resize handle — drag to change the end day */}
                      <div
                        style={{
                          position: "absolute",
                          right: -3,
                          top: 0,
                          bottom: 0,
                          width: 10,
                          cursor: "ew-resize",
                          zIndex: 2,
                        }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          handleResPointerDown(e, r, "resize");
                        }}
                      />
                      {/* Hover tooltip */}
                      {isHovered && (
                        <div
                          className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2 pointer-events-none"
                          style={{ whiteSpace: "nowrap", minWidth: 160 }}
                          ref={(el) => {
                            if (!el) return;
                            const parent = el.parentElement;
                            if (!parent) return;
                            const rect = parent.getBoundingClientRect();
                            const spaceRight = window.innerWidth - rect.right;
                            if (spaceRight >= 170) {
                              el.style.top = `${rect.top}px`;
                              el.style.left = `${rect.right + 8}px`;
                            } else {
                              el.style.top = `${rect.top}px`;
                              el.style.left = `${rect.left - 8}px`;
                              el.style.transform = "translateX(-100%)";
                            }
                          }}
                        >
                          <div className="text-h4 font-medium">
                            {r.clientName}
                          </div>
                          <div className="text-h5 text-neutral-300 mt-0.5">
                            {fmtDate(r.startDate)}
                            {r.startDate !== r.endDate
                              ? ` → ${fmtDate(r.endDate)}`
                              : ""}
                          </div>
                          {r.startHour != null && (
                            <div className="text-h5 text-neutral-300">
                              {fmtHour(r.startHour)}
                              {r.durationHours != null
                                ? ` — ${fmtHour(r.startHour + r.durationHours)}`
                                : r.returnHour != null
                                  ? ` → ${fmtHour(r.returnHour)}`
                                  : ""}
                            </div>
                          )}
                          {r.hasPickup && (
                            <div className="text-h5 text-indigo-300 mt-1">
                              Départ
                            </div>
                          )}
                          {r.hasReturn && (
                            <div className="text-h5 text-indigo-300 mt-1">
                              Retour
                            </div>
                          )}
                          <div className="text-h5 text-neutral-400 mt-1">
                            Cliquer pour modifier
                          </div>
                        </div>
                      )}
                      <div style={{ display: "contents", overflow: "hidden" }}>
                        {r.hasPickup && (
                          <div
                            className="flex flex-col items-center justify-center"
                            style={{
                              width: 20,
                              flexShrink: 0,
                              background: "#DFE2FF",
                              borderRadius: "5px 0 0 5px",
                            }}
                          >
                            <Icon1
                              name="car-restitution"
                              size={14}
                              color="#5C6BFF"
                            />
                          </div>
                        )}
                        <div
                          className="flex flex-row items-center flex-1"
                          style={{
                            padding: "3px 3px",
                            minWidth: 0,
                            overflow: "hidden",
                          }}
                        >
                          <span
                            style={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              fontSize: 12,
                              lineHeight: "100%",
                              color: "#5C6BFF",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              flex: 1,
                            }}
                          >
                            {r.clientName}
                          </span>
                        </div>
                        {r.hasReturn && (
                          <div
                            className="flex flex-col items-center justify-center"
                            style={{
                              width: 20,
                              flexShrink: 0,
                              background: "#BFC5FF",
                              borderRadius: "0 5px 5px 0",
                            }}
                          >
                            <Icon1
                              name="car-reception"
                              size={14}
                              color="#5C6BFF"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Drag preview ghost — shown in the row the drag targets */}
                {drag &&
                  drag.previewVehicleId === vehicle.id &&
                  (() => {
                    const origR = reservations.find(
                      (x) => x.id === drag.reservationId,
                    );
                    if (!origR) return null;
                    const { startDate, endDate } = applyWeekDrag(drag);
                    const gStyle = getBarStyle(origR, { startDate, endDate });
                    return (
                      <div
                        className="absolute flex flex-row items-center"
                        style={{
                          top: 0,
                          bottom: 0,
                          left: gStyle.left,
                          width: gStyle.width,
                          background: "rgba(202, 207, 255, 0.6)",
                          border: "2px dashed #5C6BFF",
                          borderRadius: 5,
                          zIndex: 40,
                          pointerEvents: "none",
                          overflow: "hidden",
                          padding: "0 6px",
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            fontSize: 12,
                            color: "#5C6BFF",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {origR.clientName}
                        </span>
                      </div>
                    );
                  })()}
              </div>
            );
          })}
        </div>
      </div>

      {editingReservation && (
        <ReservationModal
          reservation={editingReservation}
          vehicles={vehicles}
          onClose={() => setEditingReservation(null)}
          onSave={(updated) => {
            updateReservation(editingReservation.id, updated);
            setEditingReservation(null);
          }}
        />
      )}
      {editingVehicle && (
        <VehiculeModal
          vehicle={editingVehicle}
          onClose={() => setEditingVehicle(null)}
          onSave={async (updated) => {
            setEditingVehicle(null);
            const ok = await updateVehicle(updated.id, updated);
            showNotification(
              ok
                ? { type: "success", message: "Sauvegardé" }
                : { type: "error", message: "Échec de l'enregistrement du véhicule" },
            );
          }}
          onDelete={async (id) => {
            try {
              await useVehiculesStore.getState().deleteVehicle(id);
              setEditingVehicle(null);
            } catch {}
          }}
        />
      )}
    </>
  );
}

// --- Main Screen ---

const USAGE_TABS = [
  { value: "tous", label: "Tous" },
  { value: "velos", label: "Vélos" },
  { value: "pret", label: "Prêt" },
  { value: "location", label: "Location" },
];

export default function VehiculesScreen() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchVehicles = useVehiculesStore((s) => s.fetchVehicles);
  const fetchReservations = useVehiculesStore((s) => s.fetchReservations);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (orgData?.id) {
      useTagStore.getState().fetchTags(orgData.id);
      fetchVehicles(orgData.id);
      fetchReservations(orgData.id);
    }
  }, [orgData?.id]);

  // Handle ?reservation= query param: navigate to the reservation's date and highlight it
  useEffect(() => {
    const resId = searchParams.get("reservation");
    if (!resId) return;
    const {
      reservations,
      setSelectedDate,
      setViewMode,
      setTimeView,
      setHighlightedReservation,
    } = useVehiculesStore.getState();
    const res = reservations.find((r) => r.id === resId);
    if (res) {
      setSelectedDate(res.startDate);
      setViewMode("reservations");
      setTimeView("jour");
      setHighlightedReservation(resId);
      // Clear highlight after 4 seconds
      setTimeout(() => setHighlightedReservation(null), 4000);
    }
    // Remove from URL
    searchParams.delete("reservation");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, useVehiculesStore.getState().reservations.length]);

  const selectedDate = useVehiculesStore((s) => s.selectedDate);
  const setSelectedDate = useVehiculesStore((s) => s.setSelectedDate);
  const viewMode = useVehiculesStore((s) => s.viewMode);
  const setViewMode = useVehiculesStore((s) => s.setViewMode);
  const searchQuery = useVehiculesStore((s) => s.searchQuery);
  const setSearchQuery = useVehiculesStore((s) => s.setSearchQuery);
  const timeView = useVehiculesStore((s) => s.timeView);
  const setTimeView = useVehiculesStore((s) => s.setTimeView);
  const usageFilter = useVehiculesStore((s) => s.usageFilter);
  const setUsageFilter = useVehiculesStore((s) => s.setUsageFilter);
  const classFilter = useVehiculesStore((s) => s.classFilter);
  const setClassFilter = useVehiculesStore((s) => s.setClassFilter);
  const vehicles = useVehiculesStore((s) => s.vehicles);
  const reservations = useVehiculesStore((s) => s.reservations);
  const weekViewMode = useVehiculesStore((s) => s.weekViewMode);
  const setWeekViewMode = useVehiculesStore((s) => s.setWeekViewMode);
  const parcFuelTab = useVehiculesStore((s) => s.parcFuelTab);
  const setParcFuelTab = useVehiculesStore((s) => s.setParcFuelTab);
  const updateVehicle = useVehiculesStore((s) => s.updateVehicle);
  const createVehicle = useVehiculesStore((s) => s.createVehicle);
  const deleteVehicle = useVehiculesStore((s) => s.deleteVehicle);

  const [parcTable, setParcTable] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tagFilter, setTagFilter] = useState<string[]>([]);

  const handleTagToggle = (tagId: string) => {
    setTagFilter((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  const filteredVehicles = useMemo(() => {
    if (tagFilter.length === 0) return vehicles;
    return vehicles.filter((v) =>
      (v.tag_ids || []).some((id) => tagFilter.includes(id)),
    );
  }, [vehicles, tagFilter]);

  const handleVehicleTagsChange = async (
    vehicleId: string,
    tagIds: string[],
  ) => {
    try {
      const updated = await vehiclesApi.update(vehicleId, { tag_ids: tagIds });
      updateVehicle(updated.id, updated);
    } catch {
      // silent fail for inline edit
    }
  };

  useEffect(() => {
    const table = transformVehiclesToTable(
      filteredVehicles,
      parcFuelTab,
      reservations,
      handleVehicleTagsChange,
    );
    setParcTable(table);
  }, [filteredVehicles, reservations, parcFuelTab]);

  const handleVehicleClick = (row: any) => {
    const v = vehicles.find((v) => v.id === row.id);
    if (v) setEditingVehicle(v);
  };

  const handleVehicleSave = (updated: Vehicle) => {
    updateVehicle(updated.id, updated);
    const table = transformVehiclesToTable(
      vehicles.map((v) => (v.id === updated.id ? updated : v)),
      parcFuelTab,
      reservations,
      handleVehicleTagsChange,
    );
    setParcTable(table);
  };

  const handleDeleteVehicle = async (vehicleId: string) => {
    try {
      await deleteVehicle(vehicleId);
      setEditingVehicle(null);
      showNotification({ message: "Véhicule supprimé", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la suppression", type: "error" });
    }
  };

  const handleCreateVehicle = async (vehicle: Vehicle) => {
    if (!orgData?.id) return;
    const { id, ...rest } = vehicle;
    await createVehicle({ ...rest, org_id: orgData.id } as any);
    const table = transformVehiclesToTable(
      useVehiculesStore.getState().vehicles,
      parcFuelTab,
      useVehiculesStore.getState().reservations,
      handleVehicleTagsChange,
    );
    setParcTable(table);
  };

  return (
    <div
      className="flex flex-col max-h-screen overflow-auto"
      style={{ background: "#FAFAFA" }}
    >
      <div className="flex flex-col h-fit">
        {editingVehicle && (
          <VehiculeModal
            vehicle={editingVehicle}
            onClose={() => setEditingVehicle(null)}
            onSave={handleVehicleSave}
            onDelete={handleDeleteVehicle}
          />
        )}
        {showCreateModal && (
          <VehiculeModal
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreateVehicle}
          />
        )}
        {/* Title */}
        <div className="flex flex-col px-4 lg:px-10 pt-8">
          <Text1>Gestion des véhicules</Text1>
          <Text2>Gérez les véhicules de prêt et de location ici.</Text2>
        </div>

        {/* Tabs + action */}
        <div className="flex flex-row items-center justify-between px-4 lg:px-10 pt-5">
          <ChunkyTabs
            activeTab={viewMode}
            tabs={[
              { value: "reservations", label: "Réservations" },
              { value: "parc", label: "Parc" },
            ]}
            setActiveTab={(tab) => setViewMode(tab as "reservations" | "parc")}
          />
          <Button
            label="Nouveau véhicule"
            onClick={() => setShowCreateModal(true)}
            className="min-w-fit"
          />
        </div>

        {viewMode === "reservations" && (
          <div
            className="flex flex-row px-4 lg:px-10 pt-5 pb-8"
            style={{ gap: 30 }}
          >
            {/* Left sidebar */}
            <div
              className="flex flex-col"
              style={{ width: 196, flexShrink: 0, gap: 20 }}
            >
              <MiniCalendar
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                weekHighlight={timeView === "semaine"}
                showNavButtons
              />
            </div>

            {/* Right content */}
            <div
              className="flex flex-col flex-1"
              style={{ gap: 10, minWidth: 0 }}
            >
              {/* Search bar + view controls */}
              <div className="flex flex-row items-center" style={{ gap: 12 }}>
                <div style={{ width: 331 }}>
                  <SearchAlgolia
                    value={searchQuery}
                    setValue={setSearchQuery}
                    placeholder="Rechercher un véhicule..."
                    classNameCustom="w-full"
                    onSearch={async (query) => {
                      if (!orgData?.id) return [];
                      return searchVehiclesFull(query, orgData.id);
                    }}
                    onAction={(option) => {
                      if (typeof option === "object" && option.value) {
                        const v = vehicles.find((v) => v.id === option.value);
                        if (v) setEditingVehicle(v);
                        return true;
                      }
                    }}
                  />
                </div>
                <div className="shrink-0" style={{ width: "fit-content" }}>
                  <ChunkyTabs
                    activeTab={timeView}
                    tabs={[
                      { value: "semaine", label: "Semaine" },
                      { value: "jour", label: "Jour" },
                    ]}
                    setActiveTab={(tab) =>
                      setTimeView(tab as "semaine" | "jour")
                    }
                  />
                </div>
                {timeView === "semaine" && (
                  <div className="shrink-0" style={{ width: "fit-content" }}>
                    <ChunkyTabs
                      activeTab={weekViewMode}
                      tabs={[
                        { value: "dynamique", label: "Dynamique" },
                        { value: "fixe", label: "Fixe" },
                      ]}
                      setActiveTab={(tab) =>
                        setWeekViewMode(tab as "fixe" | "dynamique")
                      }
                    />
                  </div>
                )}
              </div>
              {/* Content filters */}
              <div className="flex flex-row items-center" style={{ gap: 12 }}>
                <div className="shrink-0" style={{ width: "fit-content" }}>
                  <ChunkyTabs
                    activeTab={classFilter}
                    tabs={CLASS_FILTER_TABS}
                    setActiveTab={(tab) =>
                      setClassFilter(tab as "tous" | VehicleClass)
                    }
                  />
                </div>
                <div className="shrink-0" style={{ width: "fit-content" }}>
                  <ChunkyTabs
                    activeTab={usageFilter}
                    tabs={USAGE_FILTER_TABS}
                    setActiveTab={(tab) =>
                      setUsageFilter(tab as "tous" | "velos" | "location" | "pret")
                    }
                  />
                </div>
              </div>

              {timeView === "semaine" && <ReservationGrid />}
              {timeView === "jour" && <DayReservationGrid />}
            </div>
          </div>
        )}

        {viewMode === "parc" && (
          <div className="px-4 lg:px-10 py-5 flex flex-col gap-3 w-full">
            <div className="max-w-1p2k w-full">
              {parcTable && (
                <DataGrid
                  storageKey="vehicules"
                  selectable={true}
                  table={parcTable}
                  hide_lines={true}
                  tabs={USAGE_TABS}
                  activeTab={parcFuelTab}
                  onTabChange={(tab) => {
                    setParcFuelTab(tab);
                    const table = transformVehiclesToTable(
                      vehicles,
                      tab,
                      reservations,
                      handleVehicleTagsChange,
                    );
                    setParcTable(table);
                  }}
                  onSearch={async (query) => {
                    if (!orgData?.id) return [];
                    return searchVehiclesFull(query, orgData.id);
                  }}
                  onSearchSelect={(option) => {
                    const v = vehicles.find((v) => v.id === option.value);
                    if (v) setEditingVehicle(v);
                  }}
                  onClick={handleVehicleClick}
                  handleSelectionModelChange={(ids) => setSelectedIds(ids)}
                  extraSearchContent={
                    <TagFilterBar
                      scope="vehicle"
                      selectedTagIds={tagFilter}
                      onToggle={handleTagToggle}
                    />
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
